import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import {
  STOCK_PAGE_SIZE,
  createStockMovement,
  getStockBooks,
  getStockMovements,
  updateStockMovement,
} from '../services/stock_service'
import './Stock.css'

const getValue = (record, keys, fallback = '-') =>
  keys
    .map((key) => record?.[key])
    .find((value) => value !== undefined && value !== null && value !== '') ??
  fallback
const getMovementId = (movement) =>
  getValue(
    movement,
    ['stockid', 'stock_id', 'movementid', 'movement_id', 'id'],
    '',
  )
const getBookId = (book) =>
  getValue(book, ['bookid', 'bookId', 'book_id', 'bookID', 'id', 'isbn'], '')
const getBookName = (record) =>
  getValue(record, [
    'name',
    'bookname',
    'bookName',
    'book_title',
    'bookTitle',
    'book_name',
    'title',
  ])
const getDirection = (movement) => {
  const direction = String(
    getValue(movement, ['direction', 'stock_direction', 'movement_type'], 'in'),
  ).toLowerCase()
  return ['out', 'take_out', 'take out', 'remove', 'debit'].includes(direction)
    ? 'out'
    : 'in'
}
const formatDate = (value) => {
  if (!value || value === '-') return '-'
  const date = new Date(value)
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
}

function StockModal({ movement, initialDirection, userId, onClose, onSaved }) {
  const isEdit = Boolean(movement)
  const movementBookId = String(getValue(movement, ['bookid', 'book_id'], ''))
  const [form, setForm] = useState({
    book_type: getValue(movement, ['book_type', 'bookType'], 'published_book'),
    bookid: movementBookId,
    quantity: getValue(movement, ['quantity', 'qty'], ''),
    direction: movement ? getDirection(movement) : initialDirection,
    reason: getValue(
      movement,
      ['reason', 'stock_reason', 'remarks', 'comment', 'note'],
      '',
    ),
  })
  const [books, setBooks] = useState([])
  const [isLoadingBooks, setIsLoadingBooks] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState('')
  const hasCurrentBookOption = books.some(
    (book) => String(getBookId(book)) === form.bookid,
  )

  useEffect(() => {
    const controller = new AbortController()
    let isCurrentRequest = true
    setIsLoadingBooks(true)
    setBooks([])
    setError('')

    const loadBooks = async () => {
      try {
        let bookType = form.book_type
        let items = await getStockBooks(bookType, controller.signal)
        const containsMovementBook = items.some(
          (book) => String(getBookId(book)) === movementBookId,
        )

        if (isEdit && movementBookId && !containsMovementBook) {
          const alternateType =
            bookType === 'published_book' ? 'exercise_book' : 'published_book'
          const alternateItems = await getStockBooks(
            alternateType,
            controller.signal,
          )
          if (
            alternateItems.some(
              (book) => String(getBookId(book)) === movementBookId,
            )
          ) {
            bookType = alternateType
            items = alternateItems
          }
        }

        if (!isCurrentRequest) return
        setBooks(items)
        if (bookType !== form.book_type) {
          setForm((current) => ({ ...current, book_type: bookType }))
        }
      } catch (requestError) {
        if (isCurrentRequest && requestError.name !== 'AbortError') {
          setError(requestError.message)
        }
      } finally {
        if (isCurrentRequest) setIsLoadingBooks(false)
      }
    }

    loadBooks()
    return () => {
      isCurrentRequest = false
      controller.abort()
    }
  }, [form.book_type, isEdit, movementBookId])

  const updateField = (event) => {
    const { name, value } = event.target
    setForm((current) => ({
      ...current,
      [name]: value,
      ...(name === 'book_type' ? { bookid: '' } : {}),
    }))
  }

  const submit = async (event) => {
    event.preventDefault()
    setIsSaving(true)
    setError('')
    try {
      const payload = {
        book_type: form.book_type,
        bookid: form.bookid,
        quantity: Number(form.quantity),
        direction: form.direction,
        userid: userId,
        ...(form.direction === 'out' ? { reason: form.reason.trim() } : {}),
      }
      if (isEdit) {
        await updateStockMovement(getMovementId(movement), payload)
      } else {
        await createStockMovement(payload)
      }
      onSaved()
    } catch (requestError) {
      setError(requestError.message)
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div
      className='stock-modal__backdrop'
      role='presentation'
      onMouseDown={(event) =>
        event.target === event.currentTarget && !isSaving && onClose()
      }
    >
      <section
        className='stock-modal'
        role='dialog'
        aria-modal='true'
        aria-labelledby='stock-modal-title'
      >
        <div className='stock-modal__header'>
          <div>
            <span className='app-content__eyebrow'>Stock movement</span>
            <h2 id='stock-modal-title'>
              {isEdit
                ? 'Edit stock movement'
                : form.direction === 'in'
                  ? 'Add stock'
                  : 'Take out stock'}
            </h2>
          </div>
          <button
            type='button'
            onClick={onClose}
            aria-label='Close stock form'
            disabled={isSaving}
          >
            x
          </button>
        </div>
        <div className='stock-direction' aria-label='Stock direction'>
          <button
            className={form.direction === 'in' ? 'is-active' : ''}
            type='button'
            onClick={() =>
              setForm((current) => ({ ...current, direction: 'in' }))
            }
          >
            Add stock
          </button>
          <button
            className={form.direction === 'out' ? 'is-active is-out' : ''}
            type='button'
            onClick={() =>
              setForm((current) => ({ ...current, direction: 'out' }))
            }
          >
            Take out stock
          </button>
        </div>
        {error && <div className='stock-page__error'>{error}</div>}
        <form className='stock-form' onSubmit={submit}>
          <label>
            Book type
            <select
              name='book_type'
              value={form.book_type}
              onChange={updateField}
            >
              <option value='published_book'>Published book</option>
              <option value='exercise_book'>Exercise book</option>
            </select>
          </label>
          <label>
            Books
            <select
              name='bookid'
              value={form.bookid}
              onChange={updateField}
              required
              disabled={isLoadingBooks}
            >
              <option value=''>
                {isLoadingBooks
                  ? 'Loading books...'
                  : books.length
                    ? 'Select a book'
                    : 'No books available'}
              </option>
              {isEdit && form.bookid && !hasCurrentBookOption && (
                <option value={form.bookid}>{getBookName(movement)}</option>
              )}
              {books.map((book, index) => {
                const bookId = String(getBookId(book))
                return (
                  <option key={`${bookId}-${index}`} value={bookId}>
                    {getBookName(book)}
                  </option>
                )
              })}
            </select>
          </label>
          <label>
            Quantity
            <input
              name='quantity'
              type='number'
              min='1'
              step='1'
              value={form.quantity}
              onChange={updateField}
              required
            />
          </label>
          {form.direction === 'out' && (
            <label>
              Reason
              <textarea
                name='reason'
                rows='4'
                value={form.reason}
                onChange={updateField}
                placeholder='Enter the reason for taking out stock'
                required
              />
            </label>
          )}
          <div className='stock-modal__actions'>
            <button type='button' onClick={onClose} disabled={isSaving}>
              Cancel
            </button>
            <button
              className='stock-modal__save'
              type='submit'
              disabled={isSaving}
            >
              {isSaving
                ? 'Saving...'
                : isEdit
                  ? 'Save changes'
                  : form.direction === 'in'
                    ? 'Add stock'
                    : 'Take out stock'}
            </button>
          </div>
        </form>
      </section>
    </div>
  )
}

function Stock() {
  const { user, hasPermission } = useAuth()
  const canManageStock = hasPermission('addStock')
  const userId = user?.userid ?? user?.user_id ?? user?.id ?? ''
  const [movements, setMovements] = useState([])
  const [search, setSearch] = useState('')
  const [direction, setDirection] = useState('')
  const [nextPage, setNextPage] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [isLoading, setIsLoading] = useState(true)
  const [modal, setModal] = useState(null)
  const [viewingTakeOut, setViewingTakeOut] = useState(null)
  const [error, setError] = useState('')

  const loadMovements = async (
    page = 1,
    searchTerm = search,
    directionFilter = direction,
    signal,
  ) => {
    setIsLoading(true)
    setError('')
    try {
      const result = await getStockMovements({
        page,
        search: searchTerm,
        direction: directionFilter,
        signal,
      })
      if (signal?.aborted) return
      setMovements(result.movements)
      setNextPage(result.nextPage)
      setCurrentPage(result.currentPage || page)
    } catch (requestError) {
      if (requestError.name !== 'AbortError') setError(requestError.message)
    } finally {
      if (!signal?.aborted) setIsLoading(false)
    }
  }

  useEffect(() => {
    const controller = new AbortController()
    const timer = window.setTimeout(
      () => {
        loadMovements(1, search, direction, controller.signal)
      },
      search ? 400 : 0,
    )
    return () => {
      window.clearTimeout(timer)
      controller.abort()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, direction])

  const refresh = async () => {
    setModal(null)
    await loadMovements(1)
  }
  const goToNextPage = () => {
    if (!nextPage) return
    loadMovements(nextPage)
  }
  const goToPreviousPage = () => {
    if (currentPage <= 1) return
    loadMovements(currentPage - 1)
  }

  return (
    <div className='stock-page'>
      <nav className='stock-page__breadcrumb' aria-label='Breadcrumb'>
        <Link to='/dashboard'>Dashboard</Link>
        <span aria-hidden='true'>/</span>
        <strong>Stock</strong>
      </nav>
      <header className='stock-page__header'>
        <div>
          <span className='app-content__eyebrow'>Inventory</span>
          <h1>Stock movements</h1>
          <p>Record and review stock added to or removed from the catalogue.</p>
        </div>
        {canManageStock && (
          <div className='stock-page__commands'>
            <button
              type='button'
              onClick={() => setModal({ direction: 'out' })}
            >
              Take out stock
            </button>
            <button
              className='stock-button--primary'
              type='button'
              onClick={() => setModal({ direction: 'in' })}
            >
              + Add stock
            </button>
          </div>
        )}
      </header>
      {error && <div className='stock-page__error'>{error}</div>}
      <section className='stock-table-card' aria-labelledby='stock-list-title'>
        <div className='stock-table-card__header'>
          <div>
            <h2 id='stock-list-title'>Stock history</h2>
            <p>{movements.length} movements on this page</p>
          </div>
        </div>
        <div className='stock-toolbar'>
          <label className='stock-search'>
            <span aria-hidden='true'>search</span>
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder='Search by book or user'
              aria-label='Search stock movements'
            />
          </label>
          <select
            value={direction}
            onChange={(event) => setDirection(event.target.value)}
            aria-label='Filter by stock direction'
          >
            <option value=''>All directions</option>
            <option value='IN'>Added</option>
            <option value='OUT'>Taken out</option>
          </select>
        </div>
        <div className='stock-table-wrap'>
          <table className='stock-table'>
            <thead>
              <tr>
                <th>Book</th>
                <th>Qty</th>
                <th>Direction</th>
                <th>Date</th>
                <th>Entered By</th>
                <th>
                  <span className='sr-only'>Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td className='stock-table__empty' colSpan='6'>
                    Loading stock...
                  </td>
                </tr>
              ) : movements.length ? (
                movements.map((movement, index) => {
                  const movementDirection = getDirection(movement).toUpperCase()
                  return (
                    <tr key={`${getMovementId(movement)}-${index}`}>
                      <td>
                        <strong>{getBookName(movement)}</strong>
                      </td>
                      <td>{getValue(movement, ['quantity', 'qty'], 0)}</td>
                      <td>
                        <span
                          className={`stock-badge stock-badge--${movementDirection.toLowerCase()}`}
                        >
                          {movementDirection === 'IN' ? 'Added' : 'Taken out'}
                        </span>
                      </td>
                      <td>
                        {formatDate(
                          getValue(movement, [
                            'date',
                            'created_at',
                            'createdat',
                            'stock_date',
                          ]),
                        )}
                      </td>
                      <td>
                        {getValue(movement, [
                          'entered_by',
                          'enteredBy',
                          'user',
                          'modified_user',
                          'user_fullname',
                          'username',
                          'user_name',
                        ])}
                      </td>
                      <td>
                        {movementDirection === 'OUT' && (
                          <div className='stock-actions'>
                            <button
                              type='button'
                              onClick={() => setViewingTakeOut(movement)}
                            >
                              View
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  )
                })
              ) : (
                <tr>
                  <td className='stock-table__empty' colSpan='6'>
                    No stock movements match your filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className='stock-pagination'>
          <span>
            Page {currentPage} · Showing up to {STOCK_PAGE_SIZE} movements
          </span>
          <div>
            <button
              type='button'
              onClick={goToPreviousPage}
              disabled={currentPage <= 1 || isLoading}
            >
              Previous
            </button>
            <button
              type='button'
              onClick={goToNextPage}
              disabled={!nextPage || isLoading}
            >
              Next
            </button>
          </div>
        </div>
      </section>
      {modal && (
        <StockModal
          movement={modal.movement}
          initialDirection={modal.direction || 'in'}
          userId={userId}
          onClose={() => setModal(null)}
          onSaved={refresh}
        />
      )}
      {viewingTakeOut && (
        <div
          className='stock-modal__backdrop'
          role='presentation'
          onMouseDown={(event) =>
            event.target === event.currentTarget && setViewingTakeOut(null)
          }
        >
          <section
            className='stock-modal stock-modal--takeout'
            role='dialog'
            aria-modal='true'
            aria-labelledby='takeout-comment-title'
          >
            <div className='stock-modal__header'>
              <div>
                <span className='app-content__eyebrow'>Take-out details</span>
                <h2 id='takeout-comment-title'>
                  {getBookName(viewingTakeOut)}
                </h2>
              </div>
              <button
                type='button'
                onClick={() => setViewingTakeOut(null)}
                aria-label='Close take-out details'
              >
                x
              </button>
            </div>
            <div className='stock-takeout-comment'>
              <span>Comment</span>
              <p>
                {getValue(
                  viewingTakeOut,
                  ['comment', 'reason', 'stock_reason', 'remarks', 'note'],
                  'No comment was recorded for this take-out.',
                )}
              </p>
            </div>
            <div className='stock-modal__actions'>
              <button type='button' onClick={() => setViewingTakeOut(null)}>
                Close
              </button>
            </div>
          </section>
        </div>
      )}
    </div>
  )
}

export default Stock
